import React from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import {
  SlotRenderer,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import "./heroModeAnimations.css";

const BP_CONFIG = {
  extra_large: { maxWidth: "55%", inset: 80, pb: 72, align: "start" },
  large:       { maxWidth: "60%", inset: 64, pb: 56, align: "start" },
  medium:      { maxWidth: "70%", inset: 40, pb: 44, align: "start" },
  small:       { maxWidth: "90%", inset: 24, pb: 32, align: "center" },
  extra_small: { maxWidth: "95%", inset: 16, pb: 24, align: "center" },
};

// ``cinematic`` and ``cinematic_right`` (and their animated siblings)
// share this renderer; only the horizontal anchor differs. We branch on
// the mode id so the layout cost is a single ternary — every other
// piece of the composition (animation timing, ambient motion, mobile
// stacking) is identical between variants. Adding more spatial
// variations (e.g. ``cinematic_top_right``) would just extend this
// helper.
function isRightAnchored(modeId) {
  if (!modeId) return false;
  return modeId === "cinematic_right" || modeId === "cinematic_right_animated";
}

const HeroCinematic = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // Ambient motion is mode-owned (e.g. ``cinematic_animated`` declares
  // ken-burns, ``cinematic`` declares none); it is *not* gated by the
  // entrance toggle. ``useHeroAmbient`` still suppresses motion when
  // the visitor prefers reduced motion.
  const ambientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const isMobile = config.align === "center";
  const rightAnchored = isRightAnchored(item?.hero_mode);

  const headingAnim = useHeroAnimation(isAnimated, entrance, { delay: 0 });
  const bodyAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });
  const ctaAnim = useHeroAnimation(isAnimated, entrance, { delay: 400 });

  const contentStyle = {
    maxWidth: config.maxWidth,
    ...contentInsetStyle,
    ...(isMobile
      // Mobile collapses to centered regardless of variant — the
      // distinction between left/right anchoring loses meaning at
      // narrow widths and we want the text to read centered.
      ? { paddingLeft: config.inset, paddingRight: config.inset, paddingBottom: config.pb }
      : rightAnchored
        ? { paddingRight: config.inset, paddingBottom: config.pb }
        : { paddingLeft: config.inset, paddingBottom: config.pb }),
  };

  // Outer flex container: ``items-end`` keeps content pinned to the
  // bottom; ``justify-end`` is what flips left vs right anchoring on
  // desktop. Mobile always centers (justify-center → mx-auto via the
  // inner flex), so the rightAnchored flag is a no-op at small widths.
  const horizontalJustify = isMobile
    ? "justify-center"
    : rightAnchored
      ? "justify-end"
      : "justify-start";

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
        ambientClass={ambientClass}
      />

      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={
          responsiveHeight
            ? `relative z-10 flex items-end ${horizontalJustify} w-full`
            : `absolute inset-0 z-10 flex items-end ${horizontalJustify} w-full`
        }
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        <div
          className={`flex flex-col gap-4 ${isMobile ? "items-center text-center w-full mx-auto" : ""}`}
          style={contentStyle}
        >
          <div ref={headingAnim.ref} className={headingAnim.className} style={headingAnim.style}>
            <SlotRenderer
              slotName="heading"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={1}
            />
          </div>

          <div ref={bodyAnim.ref} className={bodyAnim.className} style={bodyAnim.style}>
            <SlotRenderer
              slotName="body"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={2}
            />
          </div>

          <div ref={ctaAnim.ref} className={ctaAnim.className} style={ctaAnim.style}>
            <SlotRenderer
              slotName="cta"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroCinematic);
